
import * as mapValues from 'lodash/mapValues'
import { combineReducers } from 'redux'
import { Action } from 'actions'
import { AppState, DataState, State } from 'interfaces/state'
import { RESET_STATE } from 'actions/globalActions'

import formReducer from 'reducers/formReducer'

import error from 'reducers/app/errorReducer'
import sidebar from 'reducers/app/sidebarReducer'
import locations from 'reducers/app/locationsReducer'
import authApp from 'reducers/app/authReducer'
import insightsApp from 'reducers/app/insightsReducer'
import feedApp from 'reducers/app/feedReducer'
import viewport from 'reducers/app/viewportReducer'
import navigation from 'reducers/app/navigationReducer'
import notifications from 'reducers/app/notificationsReducer'
import onboardingApp from 'reducers/app/onboardingReducer'
import opportunitiesApp from 'reducers/app/opportunitiesReducer'
import linkageApp from 'reducers/app/linkageReducer'
import settingsApp from 'reducers/app/settingsReducer'
import creditApp from 'reducers/app/creditReducer'
import networthApp from 'reducers/app/networthReducer'
import futureApp from 'reducers/app/futureReducer'
import { assetsApp, debtsApp } from 'reducers/app/accountsReducer'
import { incomeApp, spendingApp, transactionsApp } from 'reducers/app/moneyflowReducer'
import recaptcha from 'reducers/app/recaptchaReducer'
import bountyApp from 'reducers/app/bountyReducer'
import { floatingPanesReducer } from 'reducers/app/floatingPanesReducer'
import { userImagesReducer } from 'reducers/app/userImagesReducer'
import { cropper } from 'reducers/app/cropperReducer'
import { pushNotifications } from 'reducers/app/pushNotificationsReducer'
import { deviceReducer } from 'reducers/app/deviceReducer'
import { publicDashboardAppReducer } from 'reducers/app/publicDashboardReducer'
import authData from 'reducers/data/authReducer'
import meta from 'reducers/data/metaReducer'
import member from 'reducers/data/memberReducer'
import creditData from 'reducers/data/creditReducer'
import peergroups from 'reducers/data/peergroupsReducer'
import insightsData from 'reducers/data/insightsReducer'
import networthData from 'reducers/data/networthReducer'
import linkageData from 'reducers/data/linkageReducer'
import offers from 'reducers/data/offersReducer'
import property from 'reducers/data/propertyReducer'
import flagsReducer from 'reducers/data/flagsReducer'
import { assetsData, debtsData } from 'reducers/data/accountsReducer'
import { incomeData, spendingData, transactionsData } from 'reducers/data/moneyflowReducer'
import bountyData from 'reducers/data/bountyReducer'
import feedData from 'reducers/data/feedReducer'
import flushDate from 'reducers/data/flushDateReducer'
import { transactions } from 'reducers/data/transactionsReducer'
import { publicDashboardDataReducer } from 'reducers/data/publicDashboardReducer'
import { subscriptionReducer } from 'reducers/data/subscriptionReducer'
import { futureReducer } from 'reducers/data/futureReducer'
import { advisorsData, advisorsApp } from 'components/features/coaching/advisorStore/reducers'
import { videoCallReducer } from 'components/features/videoCalls/reducers'
import { cryptoRewardsPaneReducer } from 'components/features/rewards/rewardsPane/store/reducers'
import { creditCardAppReducer, creditCardDataReducer } from 'components/features/creditCard/creditCardStore/reducers'
import { pageFiltersReducer } from 'components/features/pageFilters/reducers'
import { preferencesReducer } from 'components/features/preferences/prerefencesReducer'


const app = combineReducers<AppState>({
  // Meta
  auth: authApp,
  linkage: linkageApp,
  locations,
  userImages: userImagesReducer,

  // Internal Pages
  advisors: advisorsApp,
  bounty: bountyApp,
  assets: assetsApp,
  creditCard: creditCardAppReducer,
  creditscore: creditApp,
  debts: debtsApp,
  feed: feedApp,
  future: futureApp,
  income: incomeApp,
  networth: networthApp,
  onboarding: onboardingApp,
  opportunities: opportunitiesApp,
  settings: settingsApp,
  spending: spendingApp,
  transactions: transactionsApp,
  videoCall: videoCallReducer,

  // External Pages
  publicdashboard: publicDashboardAppReducer,

  // Global Components
  error,
  floatingPanes: floatingPanesReducer,
  insights: insightsApp,
  navigation,
  notifications,
  pageFilters: pageFiltersReducer,
  pushNotifications,
  sidebar,

  // Utility
  cropper,
  device: deviceReducer,
  viewport,
  recaptcha,
})

const data = combineReducers<DataState>({
  // Meta
  auth: authData,
  member,
  meta,
  property,
  subscription: subscriptionReducer,

  // Internal Pages
  advisors: advisorsData,
  assets: assetsData,
  bounty: bountyData,
  creditCard: creditCardDataReducer,
  creditscore: creditData,
  cryptoRewardsPane: cryptoRewardsPaneReducer,
  debts: debtsData,
  feed: feedData,
  future: futureReducer,
  income: incomeData,
  linkage: linkageData,
  networth: networthData,
  spending: spendingData,
  transactions: transactionsData,

  // External Pages
  publicdashboard: publicDashboardDataReducer,

  // Global Components
  insights: insightsData,
  offers,
  peergroups,
  recentTransactions: transactions,

  // Utility
  flushDate,
  preferences: preferencesReducer,
  flags: flagsReducer,
})

const appReducer = combineReducers<State>({
  form: formReducer,
  app,
  data,
})

const rootReducer = (state: State, action: Action) => {
  if (action.type === RESET_STATE) {
    // save a `app.navigation` state branch when we make a logout,
    // anchor on logout should remain working
    const nextAppState: AppState = {
      ...(mapValues(state.app, () => undefined)),
      navigation: state.app.navigation,
      device: state.app.device,
    } as any

    state = {
      ...state,
      app: nextAppState,
      data: undefined,
    } as any
  }

  return appReducer(state, action)
}

export default rootReducer
